import React from 'react';
import { Pagination, Button } from 'react-bootstrap';
import { formatPeriodLabel, PeriodRange } from '../../utils/period.utils';
import { SearchBar } from '../ui-tools/SearchBar';

interface OverTimeScheduleToolsPaginationProps {
  periods: PeriodRange[];
  selectedPeriodIndex: number;
  setSelectedPeriodIndex: React.Dispatch<React.SetStateAction<number>>;
  totalEmployees: number;
  searchTerm: string;
  onSearchChange: React.Dispatch<React.SetStateAction<string>>;
  onClear: () => void;
  disabled?: boolean;
}

export const OverTimeScheduleToolsPagination = ({
  periods,
  selectedPeriodIndex,
  setSelectedPeriodIndex,
  totalEmployees,
  onClear,
  onSearchChange,
  searchTerm,
  disabled = false,
}: OverTimeScheduleToolsPaginationProps) => {
  const totalPeriods = periods.length;

  if (totalPeriods === 0) return null;

  const getVisiblePages = () => {
    if (totalPeriods <= 5) {
      return Array.from({ length: totalPeriods }, (_, i) => i);
    }

    if (selectedPeriodIndex <= 1) {
      return [0, 1, 2];
    }

    if (selectedPeriodIndex >= totalPeriods - 2) {
      return [totalPeriods - 3, totalPeriods - 2, totalPeriods - 1];
    }

    return [selectedPeriodIndex - 1, selectedPeriodIndex, selectedPeriodIndex + 1];
  };

  return (
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
      <SearchBar searchTerm={searchTerm} onSearchChange={onSearchChange} onClear={onClear} disabled={disabled} />

      <div className="d-flex align-items-center gap-2">
        {totalPeriods <= 3 &&
          Array.from({ length: totalPeriods }, (_, index) => (
            <Button
              key={index}
              size="sm"
              variant={selectedPeriodIndex === index ? 'primary' : 'light'}
              className="rounded"
              disabled={disabled}
              onClick={() => setSelectedPeriodIndex(index)}
            >
              {`${formatPeriodLabel(periods[index])}`}
            </Button>
          ))}

        {totalPeriods > 3 && (
          <Pagination size="sm" className="mb-0">
            <Pagination.First
              disabled={disabled || selectedPeriodIndex === 0}
              onClick={() => setSelectedPeriodIndex(0)}
            />

            <Pagination.Prev
              disabled={disabled || selectedPeriodIndex === 0}
              onClick={() => setSelectedPeriodIndex((prev) => Math.max(prev - 1, 0))}
            />

            {totalPeriods > 5 && selectedPeriodIndex > 1 && <Pagination.Ellipsis disabled />}

            {getVisiblePages().map((index) => (
              <Pagination.Item
                key={index}
                active={selectedPeriodIndex === index}
                disabled={disabled}
                onClick={() => setSelectedPeriodIndex(index)}
              >
                {index + 1}
              </Pagination.Item>
            ))}

            {totalPeriods > 5 && selectedPeriodIndex < totalPeriods - 2 && <Pagination.Ellipsis disabled />}

            <Pagination.Next
              disabled={disabled || selectedPeriodIndex === totalPeriods - 1}
              onClick={() => setSelectedPeriodIndex((prev) => Math.min(prev + 1, totalPeriods - 1))}
            />

            <Pagination.Last
              disabled={disabled || selectedPeriodIndex === totalPeriods - 1}
              onClick={() => setSelectedPeriodIndex(totalPeriods - 1)}
            />
          </Pagination>
        )}
      </div>
    </div>
  );
};
